import { ReactNode, useState } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { JiraLogo } from "@atlaskit/logo";

import { Adg4Button } from "@/components/adg4/button/src";
import { SignupBackgroundLayout } from "@/components/signup/layouts";
import { ProductKey } from "@/components/signup/utils";
import { RovoDevAgentsLogo } from "@/components/signup/utils/get-rovo-dev-agents-logo";
import {
    useManagingOrgUserSites,
    useUserContext
} from "@/components/user-context";
import { wacToken } from "@/libraries/design/foundations";
import { FeatureExposed } from "@/utils/feature-exposed";

import { useSignupFormContext } from "../../../signup-form/hooks";
import { CobrandedProductLogo } from "../../components/variants/new-user-confluence-workspace-name";
import { Adg4SiteNameForm } from "../variants/atlas-66195-cobranding/site-name-form";

import { SitesList } from "./components/sites-list";

export const GuardReturnToProduct = () => {
    const [showSiteNaming, setShowSiteNaming] = useState(false);
    const { productName, productKey } = useSignupFormContext();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const { formatMessage } = useIntl();

    const sites = useManagingOrgUserSites();
    const { profile } = useUserContext();
    const orgId = profile?.attributes?.managingOrg?.orgId;

    const clickGoToExistingWorkspace = () => {
        setShowSiteNaming(false);
        createAnalyticsEvent({
            eventType: "ui",
            action: "clicked",
            actionSubject: "button",
            actionSubjectId: "guardGoToReturnToProductScreen",
            attributes: {
                orgId,
                productName
            }
        }).fire();
    };

    const goToExistingWorkspaceMessageValues = {
        link: (str: ReactNode) => (
            <Adg4Button css={goToLinkCss} onClick={clickGoToExistingWorkspace}>
                {str}
            </Adg4Button>
        )
    };

    const goToExistingWorkspaceMessage = formatMessage(
        {
            id: "bxpSignup.companyExpand.cobrandedReturnToJoinSites",
            defaultMessage: "or <link>join an existing site</link>"
        },
        goToExistingWorkspaceMessageValues
    );

    const clickGoToSiteNamingScreen = () => {
        setShowSiteNaming(true);
        createAnalyticsEvent({
            eventType: "ui",
            action: "clicked",
            actionSubject: "button",
            actionSubjectId: "guardGoToSiteNamingScreen",
            attributes: {
                orgId,
                productName
            }
        }).fire();
    };

    const goToSiteNamingMessageValues = {
        link: (str: ReactNode) => (
            <Adg4Button css={goToLinkCss} onClick={clickGoToSiteNamingScreen}>
                {str}
            </Adg4Button>
        )
    };

    const goToSiteNamingMessage = formatMessage(
        {
            id: "BxpSignup.SiteExpansionForm.cobrandedJiraCreateNewWorkspaceLink",
            defaultMessage: "or <link>start a new site</link>"
        },
        goToSiteNamingMessageValues
    );

    return sites?.length ? (
        <FeatureExposed
            experimentKey="signup-guard-return-to-product"
            group="treatment"
            metadata={{
                orgId
            }}
        >
            <SignupBackgroundLayout
                ProductLogo={
                    productKey === ProductKey.CONFLUENCE ? (
                        <CobrandedProductLogo />
                    ) : productKey === ProductKey.DEVAI ? (
                        <RovoDevAgentsLogo />
                    ) : (
                        <JiraLogo size="xlarge" appearance="brand" />
                    )
                }
            >
                {showSiteNaming ? (
                    <div css={containerCss}>
                        <Adg4SiteNameForm />
                        <div css={goToMessageCss}>
                            {goToExistingWorkspaceMessage}
                        </div>
                    </div>
                ) : (
                    <div css={containerCss}>
                        <SitesList sites={sites} />
                        <div css={goToMessageCss}>{goToSiteNamingMessage}</div>
                    </div>
                )}
            </SignupBackgroundLayout>
        </FeatureExposed>
    ) : null;
};

const containerCss = css({
    display: "grid",
    minHeight: "520px",
    gap: 16,
    alignContent: "space-between"
});

const goToLinkCss = css({
    backgroundColor: "transparent !important",
    border: "0 !important",
    borderRadius: "0 !important",
    color: wacToken("color.primary.blue"),
    fontSize: "inherit",
    fontWeight: "inherit",
    minHeight: "auto",
    minWidth: "auto",
    padding: "0 !important",
    textDecoration: "underline !important",
    [":hover"]: {
        color: wacToken("color.extended.blue800")
    }
});

const goToMessageCss = css({
    fontSize: 14,
    fontWeight: 500,
    textAlign: "center"
});
